import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ChevronLeft, GraduationCap, Award, BookOpen, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreditsPage = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  const [creditsData, setCreditsData] = useState({ total_credits: 0, completed_count: 0, credits_by_curriculum: {}, history: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/");
      return;
    }
    fetchCredits();
  }, [isAuthenticated]);

  const fetchCredits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/curriculum/credits`, {
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCreditsData(data);
      }
    } catch (err) {
      console.error("Failed to fetch credits:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-burgundy-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100">
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-sm hover:bg-stone-50 transition-colors"
            data-testid="credits-back-btn"
          >
            <ChevronLeft className="w-5 h-5 text-stone-600" />
          </button>
          <h1 className="text-xl font-bold text-stone-800 flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-burgundy-500" />
            연애사관학교 학점 관리
          </h1>
        </div>

        {/* Credit Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <Award className="w-8 h-8 text-amber-500" />
            </div>
            <p className="text-2xl font-bold text-amber-600">{creditsData.total_credits}</p>
            <p className="text-sm text-amber-700">총 이수학점</p>
          </div>
          
          <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4 text-center">
            <div className="flex items-center justify-center mb-2">
              <BookOpen className="w-8 h-8 text-emerald-500" />
            </div>
            <p className="text-2xl font-bold text-emerald-600">{creditsData.completed_count}</p>
            <p className="text-sm text-emerald-700">완료한 콘텐츠</p>
          </div>
          
          <div className="bg-gradient-to-br from-burgundy-50 to-burgundy-100 rounded-xl p-4 text-center col-span-2 md:col-span-1">
            <div className="flex items-center justify-center mb-2">
              <GraduationCap className="w-8 h-8 text-burgundy-500" />
            </div>
            <p className="text-2xl font-bold text-burgundy-600">
              {Object.keys(creditsData.credits_by_curriculum || {}).length}
            </p>
            <p className="text-sm text-burgundy-700">수강중인 학과</p>
          </div>
        </div>

        {/* Recent Activity */}
        {creditsData.history && creditsData.history.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h4 className="text-sm font-semibold text-stone-600 mb-3">최근 학습 이력</h4>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {creditsData.history.slice().reverse().map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between bg-stone-50 rounded-lg px-3 py-2 border border-stone-100"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                      <Award className="w-3 h-3 text-amber-500" />
                    </div>
                    <span className="text-sm text-stone-700 truncate max-w-[200px]">
                      {item.content_title}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-burgundy-500 bg-burgundy-50 px-2 py-0.5 rounded-full">
                      +{item.credits}학점
                    </span>
                    <span className="text-xs text-stone-400">
                      {new Date(item.completed_at).toLocaleDateString("ko-KR")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <Link 
              to="/love-academy"
              className="mt-4 block text-center text-sm text-burgundy-500 hover:text-burgundy-600 font-medium"
            >
              연애사관학교 바로가기
            </Link>
          </div>
        )}

        {/* Empty State */}
        {(!creditsData.history || creditsData.history.length === 0) && (
          <div className="bg-white rounded-xl p-8 text-center shadow-sm">
            <GraduationCap className="w-14 h-14 text-stone-300 mx-auto mb-3" />
            <p className="text-stone-500 mb-4">아직 이수한 콘텐츠가 없습니다</p>
            <Link 
              to="/love-academy"
              className="inline-block text-sm text-burgundy-500 hover:text-burgundy-600 font-medium bg-burgundy-50 px-5 py-2.5 rounded-xl"
            >
              연애사관학교 시작하기
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditsPage;
